import Cookies from "js-cookie";
import axios from "axios";
import moment from "moment";
import {
    Article,
    CalendarOrder,
    CalendarState,
    PrintInfo,
    PersonalDate,
    ComponentProduct,
    PersonalDateGroup,
    UserOrderInformation,
    UserPrintInfoInformation,
    OrderedCalendarSearchResult,
    SalesPages,
    OwnerPageInfo,
    CustomImage,
    LayoutFormatOptions,
    TemporaryCalendar,
    SpiralItem,
    RubberBandItem,
    ImageDetails,
    PersonalDatePresentationItem,
} from "burde";
import * as api from "util/api";
import { Dictionary } from "appz";
import { getAppInsights } from "util/telemetry-service";
import { DefaultCulture, COOKIE_USER_JWT } from "ui/constants";
import { getNumberOfSpiralsFromCalendarSize } from "ui/helper";
import { getExtraPageInfoFromExtraPages } from "page/calendar/helper";

export const getLayoutFormatOptions = async (): Promise<LayoutFormatOptions | null> => {
    try {
        const { success, data } = await api.json.get(
            "api/ui/GetLayoutFormatOptions",
        );

        if (!success) {
            return null;
        }

        return data;
    } catch {
        return null;
    }
};

export const getSavedCalendarsForUser = async (): Promise<
    Array<UserPrintInfoInformation>
> => {
    try {
        const { success, data } = await api.json.get(
            "api/calendar/getsavedcalendarsforuser",
        );

        if (!success) {
            return [];
        }

        return data;
    } catch {
        return [];
    }
};

export const getPrintInfoFromCalendarState = async (
    calendarState: CalendarState,
    coverImage: ImageDetails | null,
    backCoverImage: ImageDetails | null,
    customCoverImage: CustomImage | null,
    customBackImage: CustomImage | null,
    spiral: SpiralItem | null,
    rubberband: RubberBandItem | null,
) => {
    const calendarConfigurationId = await getCalendarConfigurationId(
        calendarState.culture !== null ? calendarState.culture : DefaultCulture,
        calendarState.calendarSize,
    );

    const numberOfSpirals = getNumberOfSpiralsFromCalendarSize(
        calendarState.calendarSize,
    );
    const culture =
        calendarState.culture !== null ? calendarState.culture : DefaultCulture;
    const article = await getCalendarArticleByCultureAndSize(
        culture,
        calendarState.calendarSize,
    );

    const printInfo: PrintInfo = {
        name: calendarState.name,
        startDate: calendarState.startDate,
        border: {
            borderImageId: calendarState.borderImageId,
            color: calendarState.borderColor,
        },
        calendarLayout:
            calendarState.layoutId != null ? calendarState.layoutId : 1,
        cover: {
            image: coverImage,
            backImage: backCoverImage,
            customImage: customCoverImage,
            backCustomImage: customBackImage,
            color: calendarState.coverColor,
            backColor: calendarState.backCoverColor,
            spiral: spiral,
            rubberBand: rubberband,
            texts: calendarState.coverTexts,
        },
        weekModules: calendarState.weekModules,
        dayModules: calendarState.dayModules,
        salesPages: null,
        metaData: [],
        personalDateGroups: [],
        personalDatePresentationItems: calendarState.personalDateItems,
        optionalDaySettings: calendarState.optionalDaySettings,
        thumbnail: null,
        thumbnailId: calendarState.thumbnailId,
        culture: calendarState.culture,
        numberOfSpirals: numberOfSpirals,
        calendarSize: calendarState.calendarSize,
        articleId: article != null ? article.id : 1,
        personalDateGroupIds: undefined,
        calendarConfigurationId:
            calendarConfigurationId !== null ? calendarConfigurationId : 1,
        sku: article !== null ? article.sku : undefined,
        calendarSizeId: undefined,
        userId: calendarState.userId,
        svgCalendarLayout: null,
        lines: calendarState.lines,
        calendarId: 0,
        externalOrderNumber: null,
        extraPages: getExtraPageInfoFromExtraPages(calendarState.extraPages),
        ownerPage: calendarState.ownerPage,
        monthDivider: calendarState.monthDivider,
        isTheme: calendarState.isTheme,
        calendarType: calendarState.calendarType,
        customNumberOfEndPages: calendarState.customNumberOfEndPages,
        fontId: calendarState.fontId,
        fontSetName: calendarState.fontSetName,
    };

    return printInfo;
};

export const getComponentProductsBySize = async (
    calendarSize: string,
): Promise<Array<ComponentProduct>> => {
    try {
        const { success, data } = await api.json.get(
            "api/calendar/getcomponentproductsbysize",
            {
                calendarSize,
            },
        );

        if (!success) {
            return [];
        }

        return data;
    } catch {
        return [];
    }
};

export const getArticleById = async (articleId: number): Promise<Article> => {
    try {
        const { success, data } = await api.json.get(
            "api/calendar/getarticlebyid",
            {
                articleId,
            },
        );

        if (!success) {
            return {} as Article;
        }

        return data as Article;
    } catch {
        return {} as Article;
    }
};

export const getCalendarOrdersForUser = async (): Promise<
    Array<UserOrderInformation>
> => {
    try {
        const { success, data, status } = await api.json.get(
            "api/calendar/getcalendarordersforuser",
        );

        if (status == 401) {
        }

        if (!success) {
            return [];
        }

        return data as Array<UserOrderInformation>;
    } catch (e) {
        console.log("e", e);
        return [];
    }
};

export const getOrderedCalendarsWithPagination = async (
    fromDate: string,
    toDate: string,
    pageNumber: number,
    searchPhrase: string,
    resultsPerPage: number,
): Promise<OrderedCalendarSearchResult> => {
    const initialPaginationValues = {
        pageNumber: 1,
        pages: 1,
        calendars: [],
        totalNumber: 0,
        success: false,
        resultsPerPage: 30,
    };

    try {
        const {
            success,
            data,
        } = await api.json.get(
            "api/calendar/getorderedcalendarswithpagination",
            { fromDate, toDate, pageNumber, searchPhrase, resultsPerPage },
        );

        if (!success) {
            return initialPaginationValues;
        }

        const returnObject = { ...data, success };

        return returnObject;
    } catch {
        return initialPaginationValues;
    }
};

export const getUserCalendars = async (): Promise<
    Dictionary<Array<CalendarOrder>>
> => {
    try {
        const { success, data } = await api.json.get(
            "api/calendar/getusercalendars",
        );

        if (!success) {
            return {};
        }

        return data as Dictionary<Array<CalendarOrder>>;
    } catch {
        return {};
    }
};

export const saveCalendarWithBurde = async (calendarState: CalendarState) => {
    try {
        const { success, data } = await api.json.post(
            "api/burdeUser/addUserDesign",
            calendarState,
        );

        if (!success) {
            return null;
        }

        const savedCalendar = data as CalendarState;
        return savedCalendar;
    } catch {
        return null;
    }
};

export const deleteCalendar = async (id: number) => {
    try {
        const { success, data } = await api.json.get(
            `api/burdeUser/deleteUserDesign`,
            {
                id,
            },
        );

        if (!success) {
            return false;
        }

        const deleted = data as boolean;
        return deleted;
    } catch {
        return false;
    }
};

export const saveCalendar = async (
    calendar: CalendarState,
    calendarId: number,
    coverImage: ImageDetails | null,
    backCoverImage: ImageDetails | null,
    customCoverImage: CustomImage | null,
    customBackImage: CustomImage | null,
    spiral: SpiralItem | null,
    rubberband: RubberBandItem | null,
) => {
    try {
        const printInfo = await getPrintInfoFromCalendarState(
            calendar,
            coverImage,
            backCoverImage,
            customCoverImage,
            customBackImage,
            spiral,
            rubberband,
        );

        if (calendarId > 0) {
            printInfo.calendarId = calendarId;
        }
        // Hotfix for broken owner page behaviour.
        if (printInfo.ownerPage) {
            printInfo.ownerPage.id = 0;
        }

        const { success, data } = await api.json.post(
            "api/calendar/savecalendar",
            printInfo,
        );

        if (!success) {
            return null;
        }

        const savedPrintInfo = data as PrintInfo;
        return savedPrintInfo;
    } catch {
        return null;
    }
};

export const getDatesets = async () => {
    const jwt = Cookies.get(COOKIE_USER_JWT);

    if (!jwt) return null; // TODO: Handle better.

    try {
        const { success, data } = await api.json.get(
            `api/burdeUser/getBurdeUserDatesets?jwt=${jwt}`,
        );

        if (!success) {
            return null;
        }

        return data;
    } catch {
        return null; // TODO: Fix this.
    }
};

export const saveDateset = async (
    personalDates: PersonalDatePresentationItem[],
) => {
    try {
        const {
            success,
            data,
        } = await api.json.post("api/burdeUser/saveDateSet", { personalDates });

        if (!success) return null;
        return data;
    } catch {
        return null;
    }
};

export const getEditableCalendarById = async (
    calendarId: number,
): Promise<PrintInfo | null | undefined> => {
    try {
        const { success, data, message } = await api.json.get(
            "api/calendar/getEditableCalendarById",
            {
                calendarId,
            },
        );

        if (!success) {
            throw message;
        }

        if (data === null || data === undefined) {
            return null;
        }

        const printInfo = data as PrintInfo;

        return printInfo;
    } catch {}
};

export const getPersonalDateGroups = async (): Promise<
    Array<PersonalDateGroup>
> => {
    try {
        const { success, data } = await api.json.get(
            "api/calendar/getPersonalDateGroups",
        );

        if (!success) {
            return [];
        }

        return data.map((g: any) => {
            return {
                ...g,
                personalDates: g.personalDates.map((d: any) => ({
                    ...d,
                    date: moment(d.date),
                })),
            };
        }) as Array<PersonalDateGroup>;
    } catch {
        return [];
    }
};

export const getLatestPersonalDateGroup = async (): Promise<PersonalDateGroup | null> => {
    try {
        const { data } = await api.json.get(
            "api/calendar/GetLatestPersonalDateGroup",
        );

        return {
            ...data,
            personalDates: formatPersonalDates(data.personalDates),
        } as PersonalDateGroup;
    } catch (e) {
        getAppInsights().trackEvent({
            name: "GetLatestPersonalDateGroup failed",
            properties: {
                error: e,
            },
        });
        return null;
    }
};

export const formatPersonalDates = (personalDates: Array<PersonalDate>) => {
    return personalDates.map((personalDate: any) => ({
        ...personalDate,
        date: moment(personalDate.date, "MMMM-YY-DD"),
    }));
};

export const uploadCoverImageToBlob = async (
    file: File,
    callback: Function,
) => {
    try {
        const config = {
            headers: { "content-type": "multipart/form-data" },
        };

        const formData = new FormData();
        formData.append("File", file, "newUploadFileName");

        var res = await axios.post(
            "api/imageupload/uploadcoverimage",
            formData,
            config,
        );

        callback(res.data);
    } catch {
        return null;
    }
};

export const saveOwnerPage = async (ownerPage: OwnerPageInfo) => {
    const { success, data } = await api.json.post(
        `/api/calendar/saveOwnerPage`,
        {
            ownerPage,
        },
    );

    if (!success || data === null || data === undefined) {
        return null;
    }

    return data as OwnerPageInfo;
};

export const createTemporaryCalendar = async (
    calendar: CalendarState,
    coverImage: ImageDetails | null,
    backCoverImage: ImageDetails | null,
    customCoverImage: CustomImage | null,
    customBackImage: CustomImage | null,
    spiral: SpiralItem | null,
    rubberband: RubberBandItem | null,
) => {
    const printInfo = await getPrintInfoFromCalendarState(
        calendar,
        coverImage,
        backCoverImage,
        customCoverImage,
        customBackImage,
        spiral,
        rubberband,
    );

    const { success, data } = await api.json.post(
        `/api/temporary-calendars/create`,
        printInfo,
    );

    if (!success || data === null || data === undefined) {
        return null;
    }

    return data as TemporaryCalendar;
};

export const getCalendarByTemporaryCalendarCode = async (code: string) => {
    const { success, data } = await api.json.get(
        `/api/temporary-calendars/code/${code}`,
    );

    if (!success || data === null || data === undefined) {
        return null;
    }

    return data as PrintInfo;
};

export const getOwnerPageCustomImageInfoFromId = async (imageId: number) => {
    try {
        const { success, data } = await api.json.get(
            "api/ImageUpload/GetOwnerPageImageById",
            {
                imageId,
            },
        );

        if (!success) return null;

        const image: CustomImage = {
            id: data.id,
            url: data.url,
        };

        return image;
    } catch (e) {
        return null;
    }
};

export const getCustomImageById = async (fileId: number) => {
    try {
        const { success, data } = await api.json.get(
            "/api/calendar/getCustomImageById",
            {
                fileId,
            },
        );

        if (!success) return null;

        return data as CustomImage;
    } catch (e) {
        return null;
    }
};

export const getSpiralByArticleNumber = async (articleNumber: string) => {
    try {
        const { success, data } = await api.json.get(
            "/api/calendar/getSpiralByArticleNumber",
            {
                articleNumber,
            },
        );

        if (!success) return null;

        return data as SpiralItem;
    } catch (e) {
        return null;
    }
};

export const getRubberbandByArticleNumber = async (articleNumber: string) => {
    try {
        const { success, data } = await api.json.get(
            "/api/calendar/getRubberbandByArticleNumber",
            {
                articleNumber,
            },
        );

        if (!success) return null;

        return data as RubberBandItem;
    } catch (e) {
        return null;
    }
};

export const getCalendarConfigurationId = async (
    culture: string,
    calendarSize: string,
) => {
    try {
        const { success, data } = await api.json.get(
            "/api/calendar/getCalendarConfigurationId",
            {
                culture,
                calendarSize,
            },
        );

        if (!success) return null;

        return data as number;
    } catch (e) {
        return null;
    }
};

export const getCalendarArticleByCultureAndSize = async (
    culture: string,
    calendarSize: string,
) => {
    try {
        const { success, data } = await api.json.get(
            "/api/calendar/getCalendarArticleByCultureAndSize",
            {
                culture,
                calendarSize,
            },
        );

        if (!success) return null;

        return data as Article;
    } catch (e) {
        return null;
    }
};
